import { useState, useEffect, useCallback } from "react";
import { API, ADMIN_SECRET, AUTH, PASS_LABELS, useClasses, toLocalDateStr } from "../adminUtils";
import { PrivateBookingModal } from "../../../components/AdminComponents";
import "../admin-base.css";
import "../admin-layout.css";
import "../admin-controls.css";
import "../admin-responsive.css";
import "./bookingsTab.css";

function RescheduleModal({ booking, onDone, onClose }) {
  const CLASSES = useClasses();
  const [newClassId, setNewClassId] = useState(booking.classId);
  const [newDate, setNewDate]       = useState(booking.date);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  const handleReschedule = async () => {
    if (!newClassId || !newDate) { setError("Please select a class and date."); return; }
    if (newClassId === booking.classId && newDate === booking.date) {
      setError("Please choose a different class or date."); return;
    }
    setLoading(true); setError(null);
    try {
      const res  = await fetch(`${API}/bookings/${booking._id}/reschedule`, {
        method: "PATCH", headers: AUTH,
        body: JSON.stringify({ newClassId, newDate }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? "Could not reschedule."); return; }
      onDone();
    } catch { setError("Something went wrong."); }
    finally   { setLoading(false); }
  };

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal" onClick={e => e.stopPropagation()}>
        <button className="adm-modal-close" onClick={onClose}>✕</button>
        <h2 className="adm-modal-title">Reschedule Booking</h2>
        <p className="adm-modal-sub">
          Student: <strong>{booking.studentName}</strong><br />
          Currently: <strong>{CLASSES.find(c => c.id === booking.classId)?.label ?? booking.classId}</strong> on <strong>{booking.date}</strong>
        </p>
        <label className="adm-modal-label">New class</label>
        <select className="adm-input adm-modal-input" value={newClassId} onChange={e => setNewClassId(e.target.value)}>
          {CLASSES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <label className="adm-modal-label">New date</label>
        <input type="date" className="adm-input adm-modal-input" value={newDate} onChange={e => setNewDate(e.target.value)} />
        {error && <p className="adm-error">{error}</p>}
        <div className="adm-modal-actions">
          <button className="adm-btn-ghost-sm" onClick={onClose}>Cancel</button>
          <button className="adm-btn-primary" onClick={handleReschedule} disabled={loading}>
            {loading ? "Rescheduling…" : "Confirm reschedule"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BookPrivateModal({ onClose, onDone }) {
  const CLASSES   = useClasses();
  const [step, setStep]           = useState("form");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [resultMsg, setResultMsg] = useState("");
  const [form, setForm] = useState({
    name: "", email: "", className: "Private 1-to-1",
    date: "", time: "", duration: "60",
    location: "studio",
    recurrence: "once", weeks: "4",
  });
  const privateClasses = CLASSES.filter(c => c.isPrivate);

  const handleChange = e => { setForm(p => ({ ...p, [e.target.name]: e.target.value })); setError(""); };

  const buildDates = () => {
    if (!form.date) return [];
    if (form.recurrence === "once") return [form.date];
    const dates = [];
    const start = new Date(form.date + "T12:00:00");
    const count = parseInt(form.weeks, 10) || 1;
    for (let i = 0; i < count; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i * 7);
      dates.push(d.toISOString().slice(0, 10));
    }
    return dates;
  };

  const handleSubmit = async () => {
    const { name, email, className, time, location } = form;
    const dates = buildDates();
    if (!name || !email || !className || !dates.length || !time) { setError("Please fill in all fields."); return; }
    setLoading(true); setError("");
    try {
      const res  = await fetch(`${API}/bookings/private`, {
        method: "POST", headers: AUTH,
        body: JSON.stringify({ name, email, className, dates, time, duration: Number(form.duration), location }),
      });
      const data = await res.json();
      if (res.status === 201) {
        const plural = data.booked > 1 ? `${data.booked} classes` : "1 class";
        setResultMsg(`Booked ${plural}. ${data.classesRemaining} remaining on pass.`);
        setStep("success"); onDone?.();
      } else if (res.status === 402 && data.needsPass) {
        setStep("needsPass");
      } else {
        setError(data.error || data.message || "Something went wrong.");
      }
    } catch { setError("Network error."); }
    finally   { setLoading(false); }
  };

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal adm-modal--private" onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3 className="adm-modal-title">Book Private Class</h3>
          <button className="adm-modal-close" onClick={onClose}>✕</button>
        </div>
        {step === "form" && (
          <div className="adm-modal-body">
            {["name","email"].map(field => (
              <div key={field} className="adm-field-group">
                <label className="adm-label">{field === "name" ? "Student name" : "Student email"}</label>
                <input className="adm-input" name={field} type={field === "email" ? "email" : "text"}
                  value={form[field]} onChange={handleChange}
                  placeholder={field === "name" ? "Full name" : "student@example.com"} />
              </div>
            ))}
            <div className="adm-field-group">
              <label className="adm-label">Class</label>
              {privateClasses.length > 0
                ? <select className="adm-input" name="className" value={form.className} onChange={handleChange}>
                    {privateClasses.map(c => <option key={c.id || c._id} value={c.title}>{c.title}</option>)}
                  </select>
                : <input className="adm-input" name="className" value={form.className} onChange={handleChange} placeholder="e.g. Private 1-to-1" />
              }
            </div>
            <div className="adm-field-group">
              <label className="adm-label">Location</label>
              <select className="adm-input" name="location" value={form.location} onChange={handleChange}>
                <option value="studio">In studio</option>
                <option value="home">Student's home</option>
                <option value="online">Online (Zoom)</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="adm-field-row">
              <div className="adm-field-group">
                <label className="adm-label">Date</label>
                <input className="adm-input" name="date" type="date"
                  min={new Date().toISOString().slice(0, 10)} value={form.date} onChange={handleChange} />
              </div>
              <div className="adm-field-group">
                <label className="adm-label">Time</label>
                <input className="adm-input" name="time" type="time" value={form.time} onChange={handleChange} />
              </div>
            </div>
            <div className="adm-field-group">
              <label className="adm-label">Duration</label>
              <select className="adm-input" name="duration" value={form.duration} onChange={handleChange}>
                <option value="60">1 hour</option>
                <option value="90">1.5 hours</option>
                <option value="120">2 hours</option>
              </select>
            </div>
            <div className="adm-field-group">
              <label className="adm-label">Repeat</label>
              <select className="adm-input" name="recurrence" value={form.recurrence} onChange={handleChange}>
                <option value="once">One-off</option>
                <option value="weekly">Weekly repeat</option>
              </select>
            </div>
            {form.recurrence === "weekly" && (
              <div className="adm-field-group">
                <label className="adm-label">Number of weeks</label>
                <select className="adm-input" name="weeks" value={form.weeks} onChange={handleChange}>
                  {[2,3,4,6,8,10,12].map(w => <option key={w} value={w}>{w} weeks</option>)}
                </select>
              </div>
            )}
            {error && <p className="adm-error">{error}</p>}
            <div className="adm-modal-actions">
              <button className="adm-btn-ghost-sm" onClick={onClose} disabled={loading}>Cancel</button>
              <button className="adm-btn-primary" onClick={handleSubmit} disabled={loading}>
                {loading ? "Booking…" : form.recurrence === "weekly" ? `Book ${form.weeks} Classes` : "Book Class"}
              </button>
            </div>
          </div>
        )}
        {step === "success" && (
          <div className="adm-modal-body adm-modal-result adm-modal-result--success">
            <span className="adm-result-icon">✓</span>
            <p className="adm-result-title">Private class booked</p>
            <p className="adm-result-sub">{resultMsg}</p>
            <button className="adm-btn-primary" onClick={onClose}>Done</button>
          </div>
        )}
        {step === "needsPass" && (
          <div className="adm-modal-body adm-modal-result adm-modal-result--warn">
            <span className="adm-result-icon">✉</span>
            <p className="adm-result-title">No active private pass</p>
            <p className="adm-result-sub">Pricing email sent to <strong>{form.email}</strong>.</p>
            <button className="adm-btn-primary" onClick={onClose}>OK</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BookingsTab() {
  const CLASSES = useClasses();
  const [date, setDate]                         = useState(toLocalDateStr(new Date()));
  const [classId, setClassId]                   = useState("");
  const [bookings, setBookings]                 = useState([]);
  const [confirmedCount, setConfirmedCount]     = useState(0);
  const [loading, setLoading]                   = useState(false);
  const [error, setError]                       = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [showPrivateModal, setShowPrivateModal] = useState(false);
  const [cancelClassResult, setCancelClassResult] = useState(null);
  const [cancellingClass, setCancellingClass]     = useState(false);
  const [showAddStudent, setShowAddStudent]       = useState(false);
  const [addStudentForm, setAddStudentForm]       = useState({ name: "", email: "", paymentType: "dropin", passCode: "" });
  const [addStudentError, setAddStudentError]     = useState("");
  const [addStudentLoading, setAddStudentLoading] = useState(false);

  // Private bookings state
  const [privateBookings, setPrivateBookings]       = useState([]);
  const [privateLoading, setPrivateLoading]         = useState(false);
  const [privateBookingTarget, setPrivateBookingTarget] = useState(null); // opens PrivateBookingModal

useEffect(() => {
  const isRealClass = CLASSES.some(c => c.id.startsWith("class_"));
  if (isRealClass) {
    const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
    const today = days[new Date().getDay()];
    const todayClass = CLASSES.find(c => c.label.startsWith(today));
    setClassId(todayClass?.id ?? CLASSES[0].id);
  }
}, [CLASSES]);

  const fetchBookings = useCallback(async () => {
    if (!classId) return;
    setLoading(true); setError(null);
    try {
      const res  = await fetch(`${API}/bookings?classId=${classId}&date=${date}`, { headers: AUTH });
      const data = await res.json();
      setBookings(data.bookings ?? []);
      setConfirmedCount(data.confirmedCount ?? 0);
    } catch { setError("Could not load bookings."); }
    finally   { setLoading(false); }
  }, [classId, date]);

  const fetchPrivateBookings = useCallback(async () => {
    setPrivateLoading(true);
    try {
      const res  = await fetch(`${API}/bookings/private?date=${date}`, { headers: AUTH });
      const data = await res.json();
      setPrivateBookings(data.bookings ?? []);
    } catch { setPrivateBookings([]); }
    finally   { setPrivateLoading(false); }
  }, [date]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);
  useEffect(() => { fetchPrivateBookings(); }, [fetchPrivateBookings]);

  const handleCancel = async id => {
    if (!window.confirm("Cancel this booking and restore pass credit?")) return;
    await fetch(`${API}/bookings/${id}/cancel`, { method: "PATCH", headers: AUTH });
    fetchBookings();
  };

  const handleCheckin = async (id, checked) => {
    await fetch(`${API}/reports/checkin/${id}`, {
      method: "PATCH", headers: AUTH,
      body: JSON.stringify({ checkedIn: checked }),
    });
    fetchBookings();
  };

  const handleAddStudent = async e => {
    e.preventDefault();
    setAddStudentError(""); setAddStudentLoading(true);
    try {
      const res = await fetch(`${API}/bookings/admin-add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${ADMIN_SECRET}` },
        body: JSON.stringify({
          studentName: addStudentForm.name, studentEmail: addStudentForm.email,
          classId, date, paymentType: addStudentForm.paymentType,
          passCode: addStudentForm.passCode || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to add student.");
      setShowAddStudent(false);
      setAddStudentForm({ name: "", email: "", paymentType: "dropin", passCode: "" });
      fetchBookings();
    } catch (err) { setAddStudentError(err.message); }
    finally       { setAddStudentLoading(false); }
  };

  const handleCancelClass = async () => {
    const cls = CLASSES.find(c => c.id === classId);
    if (!window.confirm(`Cancel ALL confirmed bookings for ${cls?.label} on ${date}?`)) return;
    setCancellingClass(true); setCancelClassResult(null);
    try {
      const res  = await fetch(`${API}/bookings/cancel-class`, {
        method: "PATCH", headers: AUTH, body: JSON.stringify({ classId, date }),
      });
      const data = await res.json();
      setCancelClassResult(res.ok ? { affected: data.affected, results: data.results } : { error: data.error ?? "Could not cancel." });
      if (res.ok) fetchBookings();
    } catch { setCancelClassResult({ error: "Network error." }); }
    finally   { setCancellingClass(false); }
  };

  const cls = CLASSES.find(c => c.id === classId);

  return (
    <div className="adm-tab-content">
      <div className="adm-filters">
        <div className="adm-filter-group">
          <label className="adm-filter-label">Date</label>
          <input type="date" className="adm-input" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div className="adm-filter-group">
          <label className="adm-filter-label">Class</label>
          <select className="adm-input" value={classId} onChange={e => setClassId(e.target.value)}>
            {CLASSES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
      </div>

      {/* ── Class bookings roster ── */}
      <div className="adm-roster-header">
        <h2 className="adm-section-title">{cls?.label}</h2>
        <div className="adm-roster-header-actions">
          <span className="adm-count-badge">{confirmedCount} booked</span>
          <button className="adm-btn-primary" onClick={() => setShowAddStudent(true)}>+ Add Student</button>
          <button className="adm-btn-primary" onClick={() => setShowPrivateModal(true)}>+ Book Private</button>
          {confirmedCount > 0 && (
            <button className="adm-btn-danger-sm" onClick={handleCancelClass} disabled={cancellingClass}>
              {cancellingClass ? "Cancelling…" : "Cancel This Class"}
            </button>
          )}
        </div>
      </div>

      {cancelClassResult && !cancelClassResult.error && (
        <div className="adm-cancel-class-result">
          <strong>{cancelClassResult.affected} booking{cancelClassResult.affected !== 1 ? "s" : ""} cancelled.</strong>
          {cancelClassResult.results?.some(r => r.creditCode) && (
            <ul className="adm-cancel-credits">
              {cancelClassResult.results.filter(r => r.creditCode).map(r => (
                <li key={r.email}>{r.name} — credit: <code>{r.creditCode}</code></li>
              ))}
            </ul>
          )}
          <button className="adm-btn-ghost-sm" onClick={() => setCancelClassResult(null)}>Dismiss</button>
        </div>
      )}
      {cancelClassResult?.error && <p className="adm-error">{cancelClassResult.error}</p>}

      {loading && <p className="adm-loading">Loading…</p>}
      {error   && <p className="adm-error">{error}</p>}

      {!loading && bookings.length > 0 && (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Payment</th><th>Status</th><th style={{textAlign:"center"}}>Checked In</th><th></th></tr>
            </thead>
            <tbody>
              {bookings.map(b => (
                <tr key={b._id} className={b.status === "cancelled" ? "row-cancelled" : ""}>
                  <td>{b.studentName}</td>
                  <td className="adm-email">{b.studentEmail}</td>
                  <td><span className="adm-type-badge">{PASS_LABELS[b.paymentType] ?? b.paymentType}</span></td>
                  <td><span className={`adm-status-badge status-${b.status}`}>{b.status}</span></td>
                  <td style={{textAlign:"center"}}>
                    {b.status === "confirmed" && (
                      <button
                        className={`adm-checkin-btn ${b.checkedIn ? "checked" : ""}`}
                        onClick={() => handleCheckin(b._id, !b.checkedIn)}
                        title={b.checkedIn ? "Mark absent" : "Mark attended"}>
                        {b.checkedIn ? "✓" : "○"}
                      </button>
                    )}
                  </td>
                  <td>
                    {b.status === "confirmed" && (
                      <div className="adm-row-actions">
                        <button className="adm-btn-ghost-sm" onClick={() => setRescheduleTarget(b)}>Reschedule</button>
                        <button className="adm-btn-danger-sm" onClick={() => handleCancel(b._id)}>Cancel</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && bookings.length === 0 && !error && (
        <p className="adm-empty">No bookings for this class and date.</p>
      )}

      {/* ── Private bookings for selected date ── */}
      <div className="adm-roster-header" style={{ marginTop: "2rem" }}>
        <h2 className="adm-section-title">Private Classes</h2>
        <span className="adm-count-badge">
          {privateBookings.filter(b => b.status === "confirmed").length} booked
        </span>
      </div>

      {privateLoading && <p className="adm-loading">Loading…</p>}

      {!privateLoading && privateBookings.length > 0 && (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Time</th><th>Duration</th><th>Location</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {privateBookings.map(b => (
                <tr key={b._id} className={b.status === "cancelled" ? "row-cancelled" : ""}>
                  <td>{b.studentName}</td>
                  <td className="adm-email">{b.studentEmail}</td>
                  <td>{b.time ?? (() => {
                    const t = b.classId.split("__")[2] ?? "";
                    if (!t) return "—";
                    const h = parseInt(t.slice(0,2), 10);
                    const m = t.slice(2);
                    const ampm = h >= 12 ? "pm" : "am";
                    return `${h % 12 || 12}:${m} ${ampm}`;
                  })()}</td>
                  <td>{b.duration ? `${b.duration} min` : "—"}</td>
                  <td>{b.location ?? "—"}</td>
                  <td><span className={`adm-status-badge status-${b.status}`}>{b.status}</span></td>
                  <td>
                    {b.status === "confirmed" && (
                      <div className="adm-row-actions">
                       <button
                        className="adm-btn adm-btn-ghost-sm"
                        onClick={() => {
                          const t = b.classId.split("__")[2] ?? "";
                          const time = t ? `${t.slice(0,2)}:${t.slice(2)}` : "";
                          setPrivateBookingTarget({ ...b, time });
                        }}>
                        Manage
                      </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!privateLoading && privateBookings.length === 0 && (
        <p className="adm-empty">No private classes on this date.</p>
      )}

      {/* ── Modals ── */}
      {rescheduleTarget && (
        <RescheduleModal booking={rescheduleTarget}
          onDone={() => { setRescheduleTarget(null); fetchBookings(); }}
          onClose={() => setRescheduleTarget(null)} />
      )}
      {showPrivateModal && (
        <BookPrivateModal onClose={() => setShowPrivateModal(false)} onDone={() => { setShowPrivateModal(false); fetchPrivateBookings(); }} />
      )}
         {privateBookingTarget && (
          <PrivateBookingModal
            event={privateBookingTarget}
            date={new Date(privateBookingTarget.date + "T12:00:00")}
            onClose={() => setPrivateBookingTarget(null)}
            onDone={() => { setPrivateBookingTarget(null); fetchPrivateBookings(); }}
          />
        )}
      {showAddStudent && (
        <div className="adm-modal-overlay" onClick={() => setShowAddStudent(false)}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-header">
              <h3 className="adm-modal-title">Add Student to Class</h3>
              <button className="adm-modal-close" onClick={() => setShowAddStudent(false)}>✕</button>
            </div>
            <div className="adm-modal-body">
              <form onSubmit={handleAddStudent}>
                <div className="adm-field-group">
                  <label className="adm-label">Name</label>
                  <input className="adm-input" value={addStudentForm.name}
                    onChange={e => setAddStudentForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="adm-field-group">
                  <label className="adm-label">Email</label>
                  <input className="adm-input" type="email" value={addStudentForm.email}
                    onChange={e => setAddStudentForm(f => ({ ...f, email: e.target.value }))} required />
                </div>
                <div className="adm-field-group">
                  <label className="adm-label">Payment Type</label>
                  <select className="adm-input" value={addStudentForm.paymentType}
                    onChange={e => setAddStudentForm(f => ({ ...f, paymentType: e.target.value }))}>
                    <option value="dropin">Drop-in</option>
                    <option value="pass4">4-Class Pass</option>
                    <option value="pass8">8-Class Pass</option>
                    <option value="member2x">Member 2x</option>
                    <option value="memberUnl">Member Unlimited</option>
                    <option value="private1">Private — 1 Class</option>
                    <option value="private3">Private — 3 Classes</option>
                    <option value="private10">Private — 10 Classes</option>
                  </select>
                </div>
                {addStudentForm.paymentType !== "dropin" && (
                  <div className="adm-field-group">
                    <label className="adm-label">Pass Code <span style={{fontWeight:400,fontSize:"0.85em"}}>(optional — e.g. YOGA-XXXX)</span></label>
                    <input className="adm-input" value={addStudentForm.passCode}
                      onChange={e => setAddStudentForm(f => ({ ...f, passCode: e.target.value }))} placeholder="YOGA-XXXX" />
                  </div>
                )}
                {addStudentError && <p className="adm-error">{addStudentError}</p>}
                <div className="adm-modal-actions">
                  <button type="button" className="adm-btn-ghost-sm" onClick={() => setShowAddStudent(false)}>Cancel</button>
                  <button type="submit" className="adm-btn-primary" disabled={addStudentLoading}>
                    {addStudentLoading ? "Adding…" : "Add Student"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}